import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import {
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  useResponsiveDialogStore,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  useFormValidationMessages,
  type FormValidationMessages,
} from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Field } from '../../../auth/field';

export enum SkillLevels {
  ABSOLUTE_BEGINNER = 'absolute_beginner',
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced',
  EXPERT = 'expert',
}

export const addSkillSchema = (VM: FormValidationMessages) =>
  z.object({
    id: z.number(),
    type: z.string(),
    name: z.string().min(1, VM.REQUIRED_FIELD_VALIDATION_MESSAGE()),
    level: z.enum([
      SkillLevels.ABSOLUTE_BEGINNER,
      SkillLevels.BEGINNER,
      SkillLevels.INTERMEDIATE,
      SkillLevels.ADVANCED,
      SkillLevels.EXPERT,
    ]),
    description: z.string().optional(),
  });

export type AddSkillSchemaType = z.infer<ReturnType<typeof addSkillSchema>>;

type JobportalAddProjectDialogProps = {
  onSubmit?: (data: AddSkillSchemaType) => void;
  onEdit?: (data: AddSkillSchemaType) => void;
  dataToEdit?: AddSkillSchemaType;
};

export const JobportalAddProjectDialog = ({
  onSubmit,
  onEdit,
  dataToEdit,
}: JobportalAddProjectDialogProps) => {
  const { t } = useTranslations();

  const close = useResponsiveDialogStore(s => s.close);

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof addSkillSchema>>>({
    defaultValues: onEdit
      ? {
          id: dataToEdit?.id,
          type: 'project',
          name: dataToEdit?.name,
          description: dataToEdit?.description,
          level: dataToEdit?.level,
        }
      : {
          id: 0,
          type: 'project',
          name: '',
          description: '',
          level: SkillLevels.BEGINNER,
        },
    resolver: zodResolver(addSkillSchema(formValidationMessages)),
  });

  const handleSubmit = form.handleSubmit(
    async data => {
      close(JobportalAddProjectDialog.id);
      if (onSubmit) {
        onSubmit(data);
      } else if (onEdit) {
        onEdit(data);
      }
    },
    errors => {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.error('Form errors:', errors);
    },
  );

  return (
    <>
      <ResponsiveDialogContent
        dialogProps={{
          className:
            'max-w-screen max-h-[94vh] w-[680px] overflow-y-auto sm:max-w-[600px] md:max-w-[680px] lg:max-w-[680px] ea-jobportal-add-skill-dialog__content ea-jobportal-dialog__content',
        }}
        drawerProps={{
          className:
            'pb-6 max-h-screen ea-jobportal-add-skill-dialog__content ea-jobportal-dialog__content',
        }}
      >
        <form onSubmit={handleSubmit} className={cn('flex flex-col gap-6 overflow-auto px-4', '')}>
          <Form {...form}>
            <ResponsiveDialogHeader
              className={cn(
                'ea-jobportal-add-skill-dialog__header ea-add-skill-dialog__header ea-jobportal-dialog__header',
              )}
            >
              <ResponsiveDialogTitle
                className={cn(
                  'font-semibold',
                  'ea-jobportal-add-skill-dialog__header-title ea-jobportal-dialog__header-title',
                )}
              >
                {t('Pridanie projektu')}
              </ResponsiveDialogTitle>
            </ResponsiveDialogHeader>
            <div
              className={cn(
                'grid grid-cols-12 gap-x-4 gap-y-3',
                'ea-jobportal-dialog__form-content ea-jobportal-add-skill-dialog__form-content',
              )}
            >
              <div className="col-span-12">
                <Field control={form.control} name="name" label={t('Názov projektu')} required />
              </div>
              <div className="col-span-12">
                <Field
                  control={form.control}
                  name="description"
                  placeholder={t('Popis projektu')}
                  type="textarea"
                />
              </div>
              <Separator
                className={cn(
                  'col-span-12 my-2 h-[1px] bg-border',
                  'ea-jobportal-dialog__separator ea-jobportal-add-skill-dialog__separator',
                )}
              />
              <div
                className={cn(
                  'col-span-12 flex items-center justify-end gap-2',
                  'ea-jobportal-dialog__buttons-wrapper ea-jobportal-add-skill-dialog__buttons-wrapper',
                )}
              >
                <Button
                  variant={'default-alt'}
                  className={cn(
                    '',
                    'ea-jobportal-dialog__cancel-button ea-jobportal-add-skill-dialog__cancel-button',
                  )}
                  onClick={() => close(JobportalAddProjectDialog.id)}
                >
                  {t('text377')}
                </Button>
                <Button
                  className={cn(
                    '',
                    'ea-jobportal-dialog__submit-button ea-jobportal-add-skill-dialog__submit-button',
                  )}
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  {t('text201')}
                </Button>
              </div>
            </div>
          </Form>
        </form>
      </ResponsiveDialogContent>
    </>
  );
};

JobportalAddProjectDialog.id = 'JOBPORTAL_ADD_SKILL_DIALOG';
